// eslint-disable @typescript-eslint/no-non-null-assertion
import { Injectable, OnDestroy } from '@angular/core';
import * as grpcWeb from 'grpc-web';
import { Observable } from 'rxjs';
import { ObservableManager } from '../types/observableManager.type';
import { MuCookieService } from './mu-cookie.service';
import { UserDataService } from './user-data.service';

class AuthInterceptor {
  private accessToken: string;

  /**
   * Create a interceptor that set the accessToken before send the request
   *
   * @param  {Observable<string>} token$ access token observable
   */
  constructor(token$: Observable<string>, private muCookieService: MuCookieService) {
    token$.subscribe(token => (this.accessToken = token));
  }

  /**
   * This interceptor put the accessToken in the metadata.
   *
   * @param request the grpc Request information
   * @param invoker the function to continue the request
   */
  intercept(request, invoker) {
    request.getMetadata().Authorization = this.muCookieService?.getCookie('token-mu')?.toString() || this.accessToken || '';
    return invoker(request);
  }
}

type GrpcUnaryCall<T, U> = (request: T, metadata: grpcWeb.Metadata, callback: (err: grpcWeb.Error, response: U) => void) => any;

interface GrpcCallAttributes<Z, U, K extends keyof Z = keyof Z> {
  callName: K;
  onBefore?: (err: grpcWeb.Error, response: U) => boolean;
}

export type GrpcCall<T, U> = (request: T) => Promise<U>;

@Injectable({
  providedIn: 'root',
})
export class GrpcFactoryService implements OnDestroy {
  private observableManager: ObservableManager = new ObservableManager();
  private enableDevTools =
    // eslint-disable-next-line no-underscore-dangle
    (window as any).__GRPCWEB_DEVTOOLS__ ||
    (() => {
      /** */
    });
  private authInterceptor: any;

  /**
   * Create the grpcFactory
   *
   * @param  {UserDataService} userDataService service to get the accessToken
   */
  constructor(userDataService: UserDataService, private muCookieService: MuCookieService) {
    this.authInterceptor = new AuthInterceptor(this.observableManager.watch(userDataService.getTokenLocal()), muCookieService);
  }

  /**
   * Create a grpc client with devtools enabled and auth inteceptor
   *
   * @param  {new(...values:any[]} grpcClass The grpc type
   * @returns T the grpc client
   */
  create<T>(grpcClass: new (...values: any[]) => T, host: string, serviceName: string): T {
    const instance = new grpcClass(host, {}, { unaryInterceptors: [this.authInterceptor], streamInterceptors: [this.authInterceptor] });
    (instance as any).muServiceName = serviceName;
    this.enableDevTools([instance]);
    return instance;
  }

  /**
   * call function after request
   *
   * @param context
   * @param operation
   * @param onBefore
   * @returns
   */
  call<Z, T, U, K extends keyof Z = keyof Z>(
    context: Z,
    operation: Z[K] extends GrpcUnaryCall<T, U> ? keyof K : GrpcUnaryCall<T, U>,
    attributes: GrpcCallAttributes<Z, U>
  ): GrpcCall<T, U> {
    const { callName, onBefore } = attributes;
    const callOperation = (operation as GrpcUnaryCall<T, U>).bind(context);
    return (request: T) => {
      return new Promise<U>((resolve, reject) => {
        callOperation(request, {}, (err: grpcWeb.Error, response: U) => {
          const payload = this.getPayload(context, callName.toString(), request);
          if (onBefore && onBefore(err, response)) {
            return reject(err);
          }
          if (err) {
            return reject(err);
          }
          const responsePayload = (response as any).toObject(false);
          const logType = this.isSuccessfulResponse(responsePayload) ? '' : 'Error';
          if (logType === 'Error') {
            payload.errorMessage = 'App Error';
          }
          payload.response = responsePayload;
          resolve(response);
        });
      });
    };
  }

  /**
   * init payload
   * @param {any} context
   * @param {string} callName
   * @param {any} request
   * @returns {any} response
   */
  getPayload(context: any, callName: string, request: any): any {
    const muServiceName = context?.muServiceName || 'No definido';
    const muCallName = callName || 'No definido';
    const requestPayload = request.toObject(false);
    const payload: any = {};
    payload.muCallName = muCallName;
    payload.muServiceName = muServiceName;
    payload.request = requestPayload;
    return payload;
  }

  ngOnDestroy() {
    this.observableManager.release();
  }

  private isSuccessfulResponse(responsePayload: any) {
    if (responsePayload.status) return responsePayload.status?.status === 1;
    if (responsePayload.statusInfo) return responsePayload.statusInfo?.status === 1;
    return false;
  }
}
